import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment-timezone';
import axios from 'axios';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './PublicCalendar.css';
import BookingFormModal from './BookingFormModal';
import { useParams } from 'react-router-dom';

const localizer = momentLocalizer(moment);

function PublicCalendar() {
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [userData, setUserData] = useState(null);
  const { userId } = useParams();
  const [businessHours, setBusinessHours] = useState(null);
  const [appointmentDuration, setAppointmentDuration] = useState(30);
  const [loading, setLoading] = useState(true);

  // Fetch user's business hours and data
  const fetchUserData = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/${userId}`);
      const user = response.data;
      if (user) {
        const ud = {
          firstname: user.firstname,
          lastname: user.lastname,
          timeZone: user.timezone || 'UTC',
        };
        setUserData(ud);

        if (user.businessHours) {
          setBusinessHours(user.businessHours);
        }
        return ud;
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error.message);
    }
    return null;
  }, [userId]);

  // Fetch blocked events (local + Google combined from backend)
  const fetchBlockedSlots = useCallback(async (coachTimezone) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/public-calendar/${userId}`);
      const tz = coachTimezone || 'UTC';

      const formattedSlots = response.data.map(slot => ({
        start: moment.utc(slot.startTime).toDate(),
        end: moment.utc(slot.endTime).toDate(),
        title: 'Not Available',
        color: 'red',
      }));

      return formattedSlots;
    } catch (error) {
      console.error('Failed to fetch blocked slots:', error.message);
      return [];
    }
  }, [userId]);

  // Load everything on mount
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const ud = await fetchUserData();
        const coachTz = ud?.timeZone || 'UTC';
        const slots = await fetchBlockedSlots(coachTz);
        setBlockedSlots(slots);
      } catch (error) {
        console.error('Failed to load calendar data:', error);
      }
      setLoading(false);
    };
    loadAll();
  }, [fetchUserData, fetchBlockedSlots]);

  // Refresh calendar after booking
  const refreshCalendar = async () => {
    const slots = await fetchBlockedSlots(userData?.timeZone || 'UTC');
    setBlockedSlots(slots);
  };

  // Handle the selection of a time slot
  const handleSelectSlot = ({ start }) => {
    const end = moment(start).add(appointmentDuration, 'minutes').toDate();
    if (isSlotAvailable(start, end)) {
      setSelectedSlot({ start, end });
      setShowBookingForm(true);
    } else {
      alert("This time slot is unavailable. Please choose another time.");
    }
  };

  // Close the booking form modal
  const handleCloseBookingForm = () => {
    setShowBookingForm(false);
    setSelectedSlot(null);
  };

  // Check if a given day is a day off
  const isDayOff = (date) => {
    if (!businessHours) return false;
    const day = moment(date).format('dddd').toLowerCase();
    return businessHours[day]?.isDayOff === true;
  };

  // Get business hours for a specific day, converted to visitor's local time
  const getBusinessHoursForDay = (date) => {
    if (!businessHours) return null;

    const day = moment(date).format('dddd').toLowerCase();
    const hours = businessHours[day];

    if (!hours || hours.isDayOff || !hours.start || !hours.end) return null;

    const coachTz = userData?.timeZone || 'UTC';
    const dateStr = moment(date).format('YYYY-MM-DD');

    // Coach's business hours in coach's timezone, then convert to local
    const startLocal = moment.tz(`${dateStr}T${hours.start}`, coachTz).local().toDate();
    const endLocal = moment.tz(`${dateStr}T${hours.end}`, coachTz).local().toDate();

    return { start: startLocal, end: endLocal };
  };

  // Check if a slot is available
  const isSlotAvailable = (start, end) => {
    // Check day off
    if (isDayOff(start)) return false;

    // Check business hours
    const bh = getBusinessHoursForDay(start);
    if (bh) {
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const endMinutes = end.getHours() * 60 + end.getMinutes();
      const bhStartMinutes = bh.start.getHours() * 60 + bh.start.getMinutes();
      const bhEndMinutes = bh.end.getHours() * 60 + bh.end.getMinutes();

      if (startMinutes < bhStartMinutes || endMinutes > bhEndMinutes) {
        return false;
      }
    }

    // Check for overlapping blocked events
    const hasConflict = blockedSlots.some(event => {
      return (
        (start >= event.start && start < event.end) ||
        (end > event.start && end <= event.end) ||
        (start <= event.start && end >= event.end)
      );
    });

    return !hasConflict;
  };

  // Style blocked events red
  const eventPropGetter = (event) => ({
    style: {
      backgroundColor: event.color || 'red',
      color: '#fff',
      borderRadius: '6px',
      border: 'none',
      fontSize: '0.8rem',
    },
  });

  // Grey out unavailable slots
  const slotPropGetter = (date) => {
    const end = moment(date).add(appointmentDuration, 'minutes').toDate();

    if (isDayOff(date)) {
      return {
        style: {
          backgroundColor: '#e8e8e8',
          pointerEvents: 'none',
        },
      };
    }

    if (!isSlotAvailable(date, end)) {
      return {
        style: {
          backgroundColor: '#f0f0f0',
          pointerEvents: 'none',
        },
      };
    }
    return {};
  };

  // Get earliest business hour start for scroll position
  const getEarliestStartTime = () => {
    if (businessHours) {
      const today = moment().format('dddd').toLowerCase();
      const hours = businessHours[today];
      if (hours && !hours.isDayOff && hours.start) {
        const [h, m] = hours.start.split(':').map(Number);
        return new Date(moment().year(), moment().month(), moment().date(), h, m, 0);
      }
    }
    return new Date(moment().year(), moment().month(), moment().date(), 8, 0, 0);
  };

  if (loading) {
    return (
      <div className="calendar-container">
        <p className="centered-text">Loading calendar...</p>
      </div>
    );
  }

  return (
    <div className="calendar-container">
      <h1>Book a session with {userData ? `${userData.firstname} ${userData.lastname}` : 'Coach'}</h1>

      <p className="centered-text">Please select a time slot to schedule an appointment.</p>

      <div className="appointment-duration-selection">
        <label>Select Appointment Duration:</label>
        <select
          value={appointmentDuration}
          onChange={(e) => setAppointmentDuration(Number(e.target.value))}
        >
          <option value={15}>15 minutes</option>
          <option value={30}>30 minutes</option>
          <option value={45}>45 minutes</option>
          <option value={60}>60 minutes</option>
        </select>
      </div>

      <Calendar
        localizer={localizer}
        events={blockedSlots}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 'calc(100vh - 300px)', minHeight: 500, margin: '20px 0' }}
        eventPropGetter={eventPropGetter}
        slotPropGetter={slotPropGetter}
        selectable
        onSelectSlot={handleSelectSlot}
        views={['month', 'week', 'day']}
        defaultView="week"
        min={new Date(1970, 1, 1, 0, 0, 0)}
        max={new Date(1970, 1, 1, 23, 59, 0)}
        step={15}
        timeslots={1}
        scrollToTime={getEarliestStartTime()}
        defaultDate={new Date()}
        formats={{
          timeGutterFormat: (date, culture, localizer) =>
            localizer.format(date, 'h:mm A', culture),
          eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'h:mm A', culture)} – ${localizer.format(end, 'h:mm A', culture)}`,
        }}
      />

      {showBookingForm && (
        <BookingFormModal
          slot={selectedSlot}
          userId={userId}
          onClose={handleCloseBookingForm}
          refreshCalendar={refreshCalendar}
        />
      )}
    </div>
  );
}

export default PublicCalendar;